import React, { useRef, useCallback } from "react";
import FilterSection from "./FilterSection";
import SchoolProfileFilterPanel from "./SchoolProfileFilterPanel";
import GcseResultsFilterPanel from "./GcseResultsFilterPanel";
import AlevelResultsFilterPanel from "./AlevelResultsFilterPanel";
import RangeSlider from "./RangeSlider";

const ICON_PROFILE = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ICON_GCSE = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ICON_ALEVEL = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const ICON_LOCATION = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SWIPE_THRESHOLD = 60;

export default function FilterDrawer({
  onClose,
  filters,
  updateFilter,
  resetFilters,
  hasActiveFilters,
  schools,
  filteredCount,
  isMobile = false,
  filterMetaError = null,
}) {
  const total = Array.isArray(schools) ? schools.length : 0;
  const shown = typeof filteredCount === "number" ? filteredCount : total;

  const touchStartY = useRef(null);
  const drawerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current == null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (deltaY > SWIPE_THRESHOLD) {
      onClose?.();
    }
  }, [onClose]);

  return (
    <aside
      ref={drawerRef}
      className="v2-right-drawer"
      aria-label="Filter schools"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {isMobile ? <div className="v2-drawer-pull-indicator" /> : null}
      <div className="v2-drawer-header">
        <div className="v2-drawer-title-wrap">
          <div className="v2-drawer-title">Filter Schools</div>
          <div className="v2-drawer-subtitle">
            {hasActiveFilters ? (
              <>
                Showing {shown} of {total}
                {" · "}
                <button
                  type="button"
                  className="v2-filter-reset-btn"
                  onClick={resetFilters}
                >
                  Clear all
                </button>
              </>
            ) : (
              <>Showing all {total} schools</>
            )}
          </div>
        </div>
        <button
          type="button"
          className="v2-icon-btn"
          title="Close"
          onClick={() => onClose?.()}
        >
          ✕
        </button>
      </div>

      {filterMetaError ? (
        <div className="v2-inline-alert" role="alert" style={{ margin: "0 12px 8px" }}>
          {filterMetaError}
        </div>
      ) : null}

      <div className="v2-drawer-scroll">
        <FilterSection
          title="School Profile &amp; Catchment"
          icon={ICON_PROFILE}
          defaultOpen
        >
          <SchoolProfileFilterPanel
            filters={filters}
            updateFilter={updateFilter}
            schools={schools}
          />
        </FilterSection>

        <FilterSection title="GCSE Results" icon={ICON_GCSE}>
          <GcseResultsFilterPanel
            filters={filters}
            updateFilter={updateFilter}
          />
        </FilterSection>

        <FilterSection title="A Level Results" icon={ICON_ALEVEL}>
          <AlevelResultsFilterPanel
            filters={filters}
            updateFilter={updateFilter}
          />
        </FilterSection>

        <FilterSection title="Location" icon={ICON_LOCATION}>
          <div className="v2-filter-panel">
            <RangeSlider
              label="Distance from Home"
              value={filters.distanceMiles}
              onChange={(v) => updateFilter("distanceMiles", v)}
              min={0}
              max={20}
              step={1}
              displayValue={filters.distanceMiles === 20 ? "Any" : `${filters.distanceMiles} mi`}
            />
          </div>
        </FilterSection>
      </div>
    </aside>
  );
}
